import { StatusTier } from "@/core/dtos/status-tier";
import {
  calculateDynamicRate,
  calculateTotalPayout,
  type MilesProgram,
} from "@/lib/miles-calculations";
import { cn } from "@/lib/utils";
import { TransactionTypes } from "@/stores";
import {
  ArrowRight,
  CalendarDays,
  Check,
  ChevronDown,
  Info,
  Lock,
  Star,
  Users,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { money, programDisplayName } from "./offer-format";

export const TRUSTPILOT_URL =
  "https://www.trustpilot.com/review/themilesmarket.com";
export const MILES_STEP = 5000;

// Colours from the client's "Offer, compact" design.
export const INK = "text-[#0C1B3A]";
export const MUTED = "text-[#51607E]";

interface OfferReadyProps {
  program: MilesProgram;
  /** Shown above the card, e.g. a failed attempt to secure the quote. */
  alert?: ReactNode;
  onAccept: () => void | Promise<void>;
  onChange: (change: { miles: number; status: StatusTier | null }) => void;
}

export const PAGE = cn(
  "w-full bg-[#F4F6FB] font-['Plus_Jakarta_Sans',system-ui,sans-serif] antialiased",
  INK,
);
export const MAIN =
  "relative mx-auto -mt-[30px] flex w-full max-w-[720px] flex-col gap-3.5 px-4 pb-6 min-[821px]:-mt-9 min-[821px]:px-6 min-[821px]:pb-12";
export const CARD =
  "rounded-[18px] border border-[#E3E8F2] bg-white px-[18px] py-5 shadow-[0_10px_30px_rgba(12,27,58,0.08)] min-[821px]:px-6 min-[821px]:py-[22px]";

export function OfferHero({
  title = "Your offer is ready",
  steps = ["Details", "Offer", "Confirm"],
}: {
  title?: string;
  steps?: [string, string, string];
}) {
  return (
    <section className="bg-[#1A3AA3] text-white">
      <div className="mx-auto max-w-[720px] px-4 pt-3 pb-[42px] min-[821px]:px-6 min-[821px]:pt-4 min-[821px]:pb-[52px]">
        <OfferSteps steps={steps} />
        <h1 className="text-2xl leading-[1.15] font-extrabold tracking-[-0.02em] min-[821px]:text-[30px]">
          {title}
        </h1>
      </div>
    </section>
  );
}

function OfferSteps({ steps }: { steps: [string, string, string] }) {
  return (
    <ol className="mb-2.5 flex items-center gap-1.5 text-[12.5px] font-bold">
      <li className="flex items-center gap-1.5 text-white">
        <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#0E7C53]">
          <Check className="h-[11px] w-[11px]" strokeWidth={3.2} aria-hidden />
        </span>
        {steps[0]}
      </li>
      <li aria-hidden className="h-0.5 w-[18px] bg-[#5C7BD6]" />
      <li
        aria-current="step"
        className="flex items-center gap-1.5 rounded-full bg-[#2B51EE] py-1 pr-2.5 pl-[5px] text-white"
      >
        <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-white text-[11px] text-[#2B51EE]">
          2
        </span>
        {steps[1]}
      </li>
      <li aria-hidden className="h-0.5 w-[18px] bg-[#5C7BD6]" />
      <li className="flex items-center gap-1.5 text-[#BCCCF6]">
        <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full border-[1.5px] border-[#5C7BD6] text-[11px]">
          3
        </span>
        {steps[2]}
      </li>
    </ol>
  );
}

export function Disclosure({
  title,
  className,
  children,
}: {
  title: string;
  className: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className={cn(
          "flex min-h-9 cursor-pointer items-center gap-1.5 py-1.5 text-[13.5px] font-semibold",
          className,
        )}
      >
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 transition-transform",
            open && "rotate-180",
          )}
          strokeWidth={2.4}
          aria-hidden
        />
        {title}
      </button>
      {open && children}
    </>
  );
}

function EditOffer({
  program,
  unit,
  onSave,
  onCancel,
}: {
  program: MilesProgram;
  unit: string;
  onSave: (change: { miles: number; status: StatusTier | null }) => void;
  onCancel: () => void;
}) {
  const [miles, setMiles] = useState(program.sanitizedMiles);
  const [status, setStatus] = useState(program.loyaltyStatus);
  const min = program.minimumValueForSell;
  const max = program.maximumValueForSell;
  const clamp = (value: number) =>
    Math.max(min || 0, max ? Math.min(max, value) : value);

  const tiers: { name: string; tier: StatusTier | null; bonus: string }[] = [
    { name: "No status", tier: null, bonus: "No bonus" },
    ...program.statusTiers.map((tier) => ({
      name: tier.name,
      tier,
      bonus: tier.percentage > 0 ? `+${tier.percentage}%` : "No bonus",
    })),
  ];
  const stepButton =
    "h-11 flex-[0_0_44px] cursor-pointer rounded-[11px] border border-[#CFD9EC] bg-white text-[22px] leading-none font-bold";

  return (
    <div className="mt-3 rounded-[14px] border border-[#DCE4F5] bg-[#F8FAFF] p-4">
      <label htmlFor="offer-miles" className="mb-2 block text-sm font-bold">
        {unit === "miles" ? "Miles" : "Points"} for sale
      </label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setMiles((value) => clamp(value - MILES_STEP))}
          aria-label={`Decrease by ${MILES_STEP.toLocaleString()} ${unit}`}
          className={stepButton}
        >
          &minus;
        </button>
        <input
          id="offer-miles"
          type="number"
          inputMode="numeric"
          value={miles}
          min={min || undefined}
          max={max || undefined}
          step={1000}
          onChange={(event) => setMiles(Number(event.target.value) || 0)}
          onBlur={() => setMiles((value) => clamp(value))}
          className="h-11 min-w-0 flex-[1_1_120px] rounded-[11px] border border-[#CFD9EC] bg-white px-3 text-[17px] font-bold"
        />
        <button
          type="button"
          onClick={() => setMiles((value) => clamp(value + MILES_STEP))}
          aria-label={`Increase by ${MILES_STEP.toLocaleString()} ${unit}`}
          className={stepButton}
        >
          +
        </button>
      </div>
      {max > 0 && (
        <p className={cn("mt-1.5 text-[12.5px]", MUTED)}>
          {min.toLocaleString()} – {max.toLocaleString()} {unit}
        </p>
      )}

      <p className="mt-4 mb-2 text-sm font-bold">Membership tier</p>
      <div className="grid grid-cols-3 gap-2">
        {tiers.map(({ name, tier, bonus }) => {
          const selected = (status?.name ?? null) === (tier?.name ?? null);
          return (
            <button
              key={name}
              type="button"
              onClick={() => setStatus(tier)}
              aria-pressed={selected}
              className={cn(
                "min-h-11 cursor-pointer rounded-[11px] p-2.5 text-left",
                selected
                  ? "border-2 border-[#2B51EE] bg-[#F5F8FF]"
                  : "border border-[#CFD9EC] bg-white",
              )}
            >
              <span className="block text-sm font-extrabold">{name}</span>
              <span className={cn("mt-0.5 block text-xs", MUTED)}>{bonus}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-3.5 flex gap-2">
        <button
          type="button"
          onClick={() => onSave({ miles: clamp(miles), status })}
          className="h-11 flex-auto cursor-pointer rounded-[11px] bg-[#2B51EE] text-[14.5px] font-bold text-white hover:bg-[#1C3BC4]"
        >
          Save changes
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="h-11 flex-none cursor-pointer rounded-[11px] border border-[#CFD9EC] bg-white px-4 text-[14.5px] font-bold text-[#1C3BC4]"
        >
          Cancel
        </button>
      </div>
      <p className={cn("mt-2.5 text-[12.5px] leading-[1.45]", MUTED)}>
        We confirm your tier when we verify your account and re-quote before any
        payment if it differs.
      </p>
    </div>
  );
}

/** A sell offer as we priced it, for a quote nothing had to adjust. */
export const OfferReady = ({
  program,
  alert,
  onAccept,
  onChange,
}: OfferReadyProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const transactionType = TransactionTypes.SELL;

  const dynamicRate = calculateDynamicRate({
    miles: program.sanitizedMiles,
    transactionType,
    sellPrices: program.sellPrices,
    buyPrices: program.buyPrices,
    fallbackRate: program.ratePerMile,
  });
  const { baseAmount, statusBonusAmount, promoCodeDiscount, totalPayout } =
    calculateTotalPayout({
      totalMiles: program.sanitizedMiles,
      dynamicRate,
      loyaltyStatus: program.loyaltyStatus,
      transactionType,
      promoCode: program.promoCode,
    });

  const programName = programDisplayName(program.name);
  const unit = program.category?.toLowerCase().includes("miles")
    ? "miles"
    : "points";
  const hasStatus =
    program.loyaltyStatus && program.loyaltyStatus.name !== "No Status";
  const offerLabel = "Your offer";
  const calcRow = "flex justify-between gap-3 border-b border-[#E6EBF5] py-2";

  return (
    <div className={PAGE}>
      <OfferHero />

      <main className={MAIN}>
        {alert}
        <section className={CARD}>
          <p
            className={cn(
              "text-[12.5px] font-bold tracking-[0.09em] uppercase",
              MUTED,
            )}
          >
            {offerLabel}
          </p>
          <p className="my-1 text-[40px] leading-[1.05] font-extrabold tracking-[-0.03em] min-[821px]:text-[50px]">
            {money(totalPayout)}
          </p>
          <p className={cn("text-sm leading-normal", MUTED)}>
            Paid the same day via PayPal once we verify your balance. Prefer
            bank transfer? You can choose on the next step.
          </p>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3.5 gap-y-2 border-y border-[#EEF1F7] py-[9px]">
            <p className="text-[14.5px] font-semibold">
              {program.sanitizedMiles.toLocaleString()} {programName} {unit}
              {hasStatus && ` · ${program.loyaltyStatus?.name} member`}
            </p>
            {!isEditing && (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="h-[34px] cursor-pointer rounded-full bg-[#EEF2FE] px-[13px] text-[13px] font-bold text-[#1C3BC4]"
              >
                Change
              </button>
            )}
          </div>

          {isEditing && (
            <EditOffer
              program={program}
              unit={unit}
              onSave={(change) => {
                onChange(change);
                setIsEditing(false);
              }}
              onCancel={() => setIsEditing(false)}
            />
          )}

          <div
            className={cn(
              "mt-3 flex flex-wrap items-center gap-x-[18px] gap-y-2.5 text-[13.5px]",
              MUTED,
            )}
          >
            <span className="inline-flex items-center gap-[7px]">
              <Users
                className="h-4 w-4 text-[#1C3BC4]"
                strokeWidth={2.2}
                aria-hidden
              />
              <span>
                <strong className={cn("font-extrabold", INK)}>50,000+</strong>{" "}
                customers served
              </span>
            </span>
            <span className="inline-flex items-center gap-[7px]">
              <CalendarDays
                className="h-4 w-4 text-[#1C3BC4]"
                strokeWidth={2.2}
                aria-hidden
              />
              <span>
                Buying miles since{" "}
                <strong className={cn("font-extrabold", INK)}>2015</strong>
              </span>
            </span>
          </div>

          <div className="mt-3 flex items-start gap-[9px] rounded-[12px] bg-[#EEF2FE] px-3 py-2.5">
            <Lock
              className="mt-px h-[18px] w-[18px] shrink-0 text-[#1C3BC4]"
              strokeWidth={2.2}
              aria-hidden
            />
            <p className="text-[13.5px] leading-[1.45]">
              Next, we'll ask for your{" "}
              <strong className="font-bold">{programName} login</strong> to
              verify your balance.
            </p>
          </div>

          <button
            type="button"
            onClick={onAccept}
            className="mt-2.5 flex min-h-11 w-full cursor-pointer items-center justify-center gap-[9px] rounded-[12px] bg-[#2B51EE] px-5 py-[15px] text-[16.5px] font-bold text-white hover:bg-[#1C3BC4]"
          >
            Accept offer &amp; continue
            <ArrowRight
              className="h-[18px] w-[18px]"
              strokeWidth={2.2}
              aria-hidden
            />
          </button>

          <div
            className={cn(
              "mt-2.5 flex items-center justify-center gap-2 text-[13.5px]",
              MUTED,
            )}
          >
            <span className="inline-flex gap-px" aria-hidden>
              {Array.from({ length: 5 }, (_, index) => (
                <Star
                  key={index}
                  className="h-3.5 w-3.5 fill-[#00B67A] text-[#00B67A]"
                  strokeWidth={0}
                />
              ))}
            </span>
            <span>
              <strong className={cn("font-bold", INK)}>4.7</strong> &middot;{" "}
              <a
                href={TRUSTPILOT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#51607E] underline hover:text-[#1C3BC4]"
              >
                1,250+ reviews on Trustpilot
              </a>
            </span>
          </div>

          <div className="mt-3">
            <Disclosure
              title="How your offer is calculated"
              className="text-[#1C3BC4]"
            >
              <div className="rounded-[12px] border border-[#DCE4F5] bg-[#F8FAFF] px-3.5 py-1.5 text-sm">
                <div className={calcRow}>
                  <span className={MUTED}>
                    Base amount &middot;{" "}
                    {program.sanitizedMiles.toLocaleString()} at{" "}
                    {money(dynamicRate * 100)} per 100
                  </span>
                  <span className="font-semibold">{money(baseAmount)}</span>
                </div>
                {statusBonusAmount > 0 && (
                  <div className={calcRow}>
                    <span className={MUTED}>
                      {program.loyaltyStatus?.name} bonus,{" "}
                      {program.loyaltyStatus?.percentage}%
                    </span>
                    <span className="font-semibold text-[#1C3BC4]">
                      +{money(statusBonusAmount)}
                    </span>
                  </div>
                )}
                {promoCodeDiscount !== 0 && (
                  <div className={calcRow}>
                    <span className={MUTED}>
                      Promo code {program.promoCode?.name}
                    </span>
                    <span className="font-semibold text-[#1C3BC4]">
                      {money(promoCodeDiscount)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between gap-3 pt-[9px] pb-1.5 text-[15px] font-extrabold">
                  <span>{offerLabel}</span>
                  <span>{money(totalPayout)}</span>
                </div>
              </div>
            </Disclosure>
            <div className="mt-2.5">
              <Disclosure
                title="Why do you need my login?"
                className="text-[#51607E]"
              >
                <ul className="flex flex-col gap-[9px] pt-1 pb-0.5 text-sm leading-normal">
                  {[
                    "We need temporary access to verify your miles balance and process your payout.",
                    "We never make changes to your account without your explicit approval.",
                    "You enter your login on the next step, after accepting this offer.",
                  ].map((line) => (
                    <li key={line} className="flex items-start gap-[9px]">
                      <Info
                        className="mt-[3px] h-4 w-4 shrink-0 text-[#51607E]"
                        strokeWidth={2.2}
                        aria-hidden
                      />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </Disclosure>
            </div>
          </div>
        </section>
      </main>

      <div className="sticky bottom-0 z-40 flex items-center justify-between gap-3 border-t border-[#E3E8F2] bg-white px-4 pt-2.5 pb-[calc(10px+env(safe-area-inset-bottom))] shadow-[0_-6px_20px_rgba(12,27,58,0.1)] min-[821px]:hidden">
        <div className="min-w-0">
          <p
            className={cn(
              "truncate text-[11px] font-bold tracking-[0.06em] uppercase",
              MUTED,
            )}
          >
            {offerLabel}
          </p>
          <p className="text-[21px] leading-[1.1] font-extrabold tracking-[-0.02em]">
            {money(totalPayout)}
          </p>
          <p className={cn("mt-0.5 text-[11.5px] whitespace-nowrap", MUTED)}>
            Next: account verification
          </p>
        </div>
        <button
          type="button"
          onClick={onAccept}
          className="flex h-[46px] flex-none cursor-pointer items-center gap-1.5 rounded-[12px] bg-[#2B51EE] px-4 text-[14.5px] font-bold whitespace-nowrap text-white hover:bg-[#1C3BC4]"
        >
          Accept &amp; continue
          <ArrowRight className="h-4 w-4" strokeWidth={2.4} aria-hidden />
        </button>
      </div>
    </div>
  );
};

/** Holds the offer's shape while the quote loads, so nothing jumps. */
export const OfferReadySkeleton = () => {
  const bone = "animate-pulse rounded-md bg-[#EEF1F7]";
  return (
    <div className={PAGE} aria-busy="true" aria-label="Loading your offer">
      <OfferHero />
      <main className={MAIN}>
        <section className={CARD}>
          <div className={cn(bone, "h-4 w-24")} />
          <div className={cn(bone, "my-2 h-10 w-48 min-[821px]:h-[50px]")} />
          <div className={cn(bone, "h-4 w-full")} />
          <div className={cn(bone, "mt-2 h-4 w-2/3")} />
          <div className="mt-3 flex items-center justify-between border-y border-[#EEF1F7] py-[9px]">
            <div className={cn(bone, "h-5 w-56")} />
            <div className={cn(bone, "h-[34px] w-[78px] rounded-full")} />
          </div>
          <div className="mt-3 flex flex-wrap gap-x-[18px] gap-y-2.5">
            <div className={cn(bone, "h-5 w-48")} />
            <div className={cn(bone, "h-5 w-44")} />
          </div>
          <div className={cn(bone, "mt-3 h-[42px] w-full rounded-[12px]")} />
          <div
            className={cn(
              bone,
              "mt-2.5 h-[52px] w-full rounded-[12px] bg-[#DCE4F5]",
            )}
          />
          <div className={cn(bone, "mx-auto mt-3 h-4 w-64")} />
          <div className={cn(bone, "mt-5 h-4 w-52")} />
          <div className={cn(bone, "mt-5 h-4 w-44")} />
        </section>
      </main>
    </div>
  );
};
